/**
 * Digit masking helpers — iter-226 (XYZ theory, strict — POSITION order LTR).
 *
 * A merchant can hide certain digits in a phone or plate number before
 * publishing. Hidden digits are replaced with SEQUENTIAL LETTERS X, Y, Z
 * ONLY — no W/V/U. Rules:
 *   - Two hidden digits sharing the same numeric VALUE share the SAME letter.
 *   - Letters are assigned by POSITION ORDER (left-to-right in the number,
 *     scanning only hidden positions). First distinct value encountered → X,
 *     next distinct → Y, third distinct → Z. Click order is IRRELEVANT.
 *   - **HARD CAP: at most 3 DISTINCT digit values may be hidden.** Attempting
 *     to hide a 4th distinct value must be rejected with the message
 *     "لا يمكن إخفاء الرقم لعدم وجود تكرار أرقام زائد".
 *   - Count: any 0..fullLen is allowed as long as the ≤3-distinct rule holds.
 *     (Historical `max` field kept only for backward-compat; not enforced.)
 */

export const MASK_LETTERS = ['X', 'Y', 'Z'];
export const MAX_DISTINCT_VALUES = 3;

export const MASK_LIMITS = {
  phone: { max: 5, len: 7 },
  plate: { max: 2, len: 5 },
};

export const MASK_ERRORS = {
  tooManyDistinct: {
    ar: 'لا يمكن إخفاء الرقم لعدم وجود تكرار أرقام زائد',
    en: 'Cannot hide: too many distinct digit values (max 3: X, Y, Z)',
  },
  countOutOfRange: {
    ar: 'عدد الأرقام المخفية غير مسموح',
    en: 'Illegal number of hidden digits',
  },
};

/**
 * Compute the set of distinct digit values at the given hidden positions.
 */
function distinctHiddenValues(value, hiddenIndices) {
  const chars = String(value).split('');
  const s = new Set();
  (hiddenIndices || []).forEach(i => {
    if (i >= 0 && i < chars.length) s.add(chars[i]);
  });
  return s;
}

/**
 * Apply the strict XYZ mask.
 * Letter assignment follows LEFT-TO-RIGHT POSITION order through the number.
 * First hidden position (leftmost) determines X's digit-value; the next
 * distinct hidden value going right → Y; the third → Z.
 */
export function applyMask(value, hiddenIndices) {
  if (!value) return '';
  if (!Array.isArray(hiddenIndices) || hiddenIndices.length === 0) return String(value);

  const chars = String(value).split('');
  const hiddenSet = new Set(hiddenIndices.map(Number).filter(n => Number.isInteger(n)));

  const map = {};
  let nextLetter = 0;
  // Scan LTR through the number itself — not through hiddenIndices — so the
  // letter mapping is deterministic regardless of insertion / click order.
  for (let i = 0; i < chars.length; i++) {
    if (!hiddenSet.has(i)) continue;
    const v = chars[i];
    if (v in map) continue;
    map[v] = MASK_LETTERS[nextLetter] ?? 'X';
    nextLetter += 1;
  }

  return chars.map((ch, i) => (hiddenSet.has(i) ? (map[ch] ?? 'X') : ch)).join('');
}

/**
 * True iff (count) is a legal hide-count.
 * New rule (iter-226): any 0..fullLen is fine — the ≤3-distinct cap is the
 * real constraint. This is what the merchant expects: "if my number has
 * only 3 distinct digits, I should be able to hide all 7."
 */
export function isLegalHideCount(count, kind) {
  const limits = MASK_LIMITS[kind];
  if (!limits) return false;
  if (count < 0) return false;
  return count <= limits.len;
}

/**
 * Validate that a proposed hidden-indices array is fully legal.
 * Returns { ok: true } OR { ok: false, error: {ar, en} }.
 */
export function validateHiddenIndices(value, hiddenIndices, kind) {
  const arr = Array.isArray(hiddenIndices) ? hiddenIndices : [];
  if (!isLegalHideCount(arr.length, kind)) {
    return { ok: false, error: MASK_ERRORS.countOutOfRange };
  }
  const distinct = distinctHiddenValues(value, arr);
  if (distinct.size > MAX_DISTINCT_VALUES) {
    return { ok: false, error: MASK_ERRORS.tooManyDistinct };
  }
  return { ok: true };
}

/**
 * Toggle whether a given position is hidden.
 * Enforces ONLY the ≤3-distinct cap. Count is bounded naturally by fullLen.
 * (Click order no longer matters for letter mapping since applyMask now
 * scans LTR, so we can sort the array to keep it tidy for storage.)
 *
 * Returns { indices, error? } — if `error` is set, the toggle was rejected.
 */
export function toggleHiddenDigit(current, index, value, kind) {
  const limits = MASK_LIMITS[kind];
  const fullLen = limits?.len ?? String(value).length;
  const cur = Array.isArray(current) ? [...current] : [];
  const isHidden = cur.includes(index);

  if (isHidden) {
    return { indices: cur.filter(i => i !== index).sort((a, b) => a - b) };
  }

  const next = [...cur, index].sort((a, b) => a - b);

  if (next.length > fullLen) return { indices: cur, error: MASK_ERRORS.countOutOfRange };

  const distinct = distinctHiddenValues(value, next);
  if (distinct.size > MAX_DISTINCT_VALUES) {
    return { indices: cur, error: MASK_ERRORS.tooManyDistinct };
  }

  return { indices: next };
}

// Backwards-compat (unused by new logic)
export const MAX_HIDDEN = {
  phone: MASK_LIMITS.phone.max,
  plate: MASK_LIMITS.plate.max,
};
